import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  type AnalyzedRow,
  type CsvRowActionFilter,
  getCsvImportRows,
} from '@/lib/csv-import/api';

const EMPTY_ROWS: readonly AnalyzedRow[] = Object.freeze([]);
const STALE_TIME_MS = 30_000;

export interface UseAnalyzedRowsPageArgs {
  orgId: number;
  importId: number;
  action?: CsvRowActionFilter;
  fileId?: string;
  limit: number;
  offset: number;
  enabled?: boolean;
}

/**
 * Fetches one page of analyzed rows for a large import via the paginated
 * `/rows` endpoint. Disabled until `importId` is known (or `enabled` is
 * explicitly false). Uses `keepPreviousData` so paging doesn't flash empty
 * while the next page loads.
 *
 * @returns `{ rows, count, isLoading, isError, error, isFetching,
 *   isPlaceholderData, refetch }` — a stable object reference that changes only
 *   when query state changes.
 */
export function useAnalyzedRowsPage({
  orgId,
  importId,
  action,
  fileId,
  limit,
  offset,
  enabled,
}: UseAnalyzedRowsPageArgs) {
  const query = useQuery({
    queryKey: [
      'csvImportRows',
      orgId,
      importId,
      action ?? null,
      fileId ?? null,
      limit,
      offset,
    ] as const,
    queryFn: () =>
      getCsvImportRows(orgId, importId, { action, fileId, limit, offset }),
    enabled: (enabled ?? true) && Number.isFinite(importId),
    staleTime: STALE_TIME_MS,
    placeholderData: keepPreviousData,
  });

  return useMemo(
    () => ({
      rows: query.data?.results ?? (EMPTY_ROWS as AnalyzedRow[]),
      count: query.data?.count ?? 0,
      isLoading: query.isLoading,
      isError: query.isError,
      error: query.error,
      isFetching: query.isFetching,
      // True while `keepPreviousData` is serving the previous query key's data
      // because the current key (new filter/offset) hasn't resolved yet. The
      // returned `rows`/`count` still describe the OLD request, so consumers
      // must not render them under the new filter label/range.
      isPlaceholderData: query.isPlaceholderData,
      refetch: query.refetch,
    }),
    [
      query.data,
      query.isLoading,
      query.isError,
      query.error,
      query.isFetching,
      query.isPlaceholderData,
      query.refetch,
    ]
  );
}
